import {
	IconArrowUpRightOutline18 as ArrowUpRight,
	IconCheckOutline18 as Check,
	IconCodeCompareOutline18 as CodeCompare,
	IconCopyOutline18 as Copy,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import type { DocsProposalChange } from "./message-enrichments"
import { useCopiedFlag } from "../../hooks/useCopiedFlag"
import { Badge } from "../badge"
import { Button } from "../button"
import {
	getRewritePreviewTypography,
	REWRITE_PREVIEW_SCALE,
} from "./content-rewrite-card"

export interface DocsProposalCardProps {
	kind: DocsProposalChange["kind"]
	title: string
	/** Merged article body. Null while the proposal body is still loading. */
	html: string | null
	onPreview?: () => void
	onOpen: () => void
	/** Declines the change without leaving the card's surface. */
	onSkip?: () => void
	className?: string
}

const KIND_LABEL: Record<DocsProposalChange["kind"], string> = {
	CREATE: "New",
	EDIT: "Edit",
	DELETE: "Delete",
}

const htmlToText = (html: string) => {
	if (typeof document === "undefined") return html
	const el = document.createElement("div")
	el.innerHTML = html
	return (el.innerText || el.textContent || "").trim()
}

// A staged docs change, rendered as the same preview card a copilot rewrite
// gets: the proposed body under its title, with Preview opening the diff
// against the live article and Open landing on the review screen.
export const DocsProposalCard = ({
	kind,
	title,
	html,
	onPreview,
	onOpen,
	onSkip,
	className,
}: DocsProposalCardProps) => {
	const [copied, markCopied] = useCopiedFlag(1500)

	const handleCopy = () => {
		if (!html) return
		void navigator.clipboard.writeText(htmlToText(html))
		markCopied()
	}

	return (
		<div
			className={cn(
				"-mx-2 mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-750",
				className,
			)}
		>
			<div className="flex items-center gap-1.5 border-b border-gray-200 px-3.5 py-2 dark:border-gray-700/80">
				<Badge variant="light-gray" size="sm" className="flex-none">
					{KIND_LABEL[kind]}
				</Badge>
				<span className="min-w-0 truncate text-sm font-medium tracking-tight text-primary">
					{title}
				</span>
			</div>
			{kind === "DELETE" ? (
				<div className="px-3.5 py-2.5 text-[0.8125rem] text-secondary">
					The agent marked this article for deletion.
				</div>
			) : html === null ? (
				<div className="px-3.5 py-2.5 text-[0.8125rem] text-tertiary">
					Loading the drafted article
				</div>
			) : (
				<div className="relative">
					<div
						className={cn(
							getRewritePreviewTypography("article"),
							REWRITE_PREVIEW_SCALE,
							"max-h-80 overflow-y-auto px-3.5 py-2.5 [&>*:first-child]:!mt-0 [&_a]:[overflow-wrap:anywhere]",
						)}
						dangerouslySetInnerHTML={{ __html: html }}
					/>
					<div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent dark:from-gray-750" />
				</div>
			)}
			<div className="flex items-center justify-between gap-1 px-3 py-2">
				<Button
					size="sm"
					variant="ghost"
					onClick={handleCopy}
					disabled={!html}
					Icon={copied ? Check : Copy}
				>
					{copied ? "Copied" : "Copy"}
				</Button>
				<div className="flex items-center gap-1">
					{onSkip && (
						<Button size="sm" variant="ghost" onClick={onSkip}>
							Skip
						</Button>
					)}
					{onPreview && (
						<Button
							size="sm"
							variant="ghost"
							onClick={onPreview}
							Icon={CodeCompare}
						>
							Preview
						</Button>
					)}
					<Button
						size="sm"
						variant="ghost"
						onClick={onOpen}
						iconRight={ArrowUpRight}
					>
						Review
					</Button>
				</div>
			</div>
		</div>
	)
}
